import { ContextMenuHandler } from "@asup/context-menu";
import { CommentableEntityType } from "functions/api/notifications/interface";
import { ReactNode, useMemo } from "react";
import { ChatLeftDotsFill } from "react-bootstrap-icons";
import "./Comments.css";
import { useComments } from "./useComments";

interface CommentScopeProps {
  id: string;
  activeContext: CommentableEntityType;
  children: ReactNode;
  className?: string;
  commentLabel: string;
  disabled?: boolean;
  iconPosition?: { top?: string; right?: string; bottom?: string; left?: string };
  noIcon?: boolean;
  style?: React.CSSProperties;
  wrap?: boolean;
}

/**
 * Wrapper component that adds comment functionality to any child element.
 *
 * @param props - Component props.
 * @param props.id - The id of the element to attach comments to.
 * @param props.activeContext - The active comment view context.
 * @param props.children - Children to render inside the scope.
 * @param props.className - Optional class name to apply.
 * @param props.commentLabel - Label for the comment action.
 * @param props.disabled - When true, disable commenting.
 * @param props.iconPosition - Optional position for the comment icon.
 * @param props.noIcon - When true, hide the comment icon.
 * @param props.style - Optional inline style.
 * @param props.wrap - When true, wrap children with fit-content sizing.
 */
export const CommentScope = ({
  id,
  activeContext,
  children,
  className = "",
  commentLabel,
  disabled = false,
  iconPosition = { top: "-6px", left: "20px" },
  noIcon = false,
  style,
  wrap = false,
}: CommentScopeProps): React.ReactElement => {
  // Get contexts
  const { comments, commentPanelStatus, setCommentPanelStatus, createComment } =
    useComments(activeContext);

  // Handle border
  const activeBorder = useMemo(
    () => commentPanelStatus?.showComments && commentPanelStatus?.activeRelatedTo === id,
    [commentPanelStatus?.activeRelatedTo, id, commentPanelStatus?.showComments],
  );

  const targetComment = useMemo(() => comments?.find((c) => c.relatedTo === id), [comments, id]);

  /**
   * Create a new comment and open the comment panel for the related target.
   *
   * @param relatedTo - The id of the element the comment relates to.
   */
  const addNewComment = (relatedTo: string) => {
    /* istanbul ignore else */
    if (commentPanelStatus && setCommentPanelStatus) {
      const comment = createComment(undefined, relatedTo);
      /* istanbul ignore else */
      if (comment) {
        // Focus if the comments panel is open, otherwise it will be focused by the CommentPanel event
        /* istanbul ignore else */
        if (commentPanelStatus.showComments === true) {
          /**
           * Focus the newly created comment editor element if present.
           */
          const doFocus = () => {
            // Get element
            const el = document.getElementById(`comment-${comment.id}-editable`);
            // Check if element exists and is focused
            /* istanbul ignore else */
            if (el && document.activeElement !== el) {
              el.focus();
            }
          };
          // Run focus in timeout to ensure the comment is visible
          setTimeout(doFocus, 100);
        }
        setCommentPanelStatus({
          ...commentPanelStatus,
          activeRelatedTo: comment.relatedTo ?? "",
          highlightCommentId: comment.id,
          showComments: true,
        });
      }
    }
  };

  return (
    <ContextMenuHandler
      menuItems={
        !disabled
          ? [
              {
                label: commentLabel,
                action: () => addNewComment(id),
              },
            ]
          : []
      }
      style={
        wrap ? { width: "fit-content", height: "fit-content" } : { width: "100%", height: "100%" }
      }
    >
      {!noIcon &&
        !disabled &&
        setCommentPanelStatus &&
        commentPanelStatus &&
        comments &&
        comments.findIndex((c) => c.relatedTo === id) !== -1 &&
        targetComment && (
          <div className="position-relative">
            <ChatLeftDotsFill
              size="12"
              aria-label="Show comments"
              style={{
                ...iconPosition,
                position: "absolute",
                zIndex: 1,
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCommentPanelStatus({
                  ...commentPanelStatus,
                  activeRelatedTo: targetComment.relatedTo!,
                  highlightCommentId: targetComment.id,
                  showComments: true,
                });
                // Focus on the comment in timeout, ensure it is visible
                setTimeout(() => {
                  document.getElementById(`comment-${targetComment.id}-box`)?.focus();
                }, 250);
              }}
            />
          </div>
        )}
      <div
        className={`comment-limit-border ${!disabled && activeBorder ? "active" : ""} ${className}`}
        style={style}
      >
        {children}
      </div>
    </ContextMenuHandler>
  );
};

CommentScope.displayName = "CommentScope";
